// Contract types for the Nebisys HTTP API (spec 11, sections 4.1 to 4.8).

export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };
export type JsonObject = { [key: string]: JsonValue };
export type JsonSchema = { [key: string]: JsonValue };

// ---- Blueprint format (owner: 01 Blueprint model) ----

/** `info`: feature spec 17 (network and IPAM diagnostics such as `IPAM_REGION_BLOCK_ADDED`). */
export type Severity = 'error' | 'warning' | 'info';

export interface Diagnostic {
  severity: Severity;
  code: string;
  path: string;
  message: string;
  resourceId?: string | null;
}

export type ParameterType = 'string' | 'number' | 'boolean';
export type ParameterValue = string | number | boolean;

export interface ParameterDefinition {
  type: ParameterType;
  default?: ParameterValue;
  allowed?: ParameterValue[];
  minimum?: number;
  maximum?: number;
  description?: string;
}

export interface Placement {
  provider?: string;
  region?: string;
}

export interface BlueprintResource {
  id: string;
  type: string;
  name?: string;
  description?: string;
  placement?: Placement;
  properties?: JsonObject;
  providerOverrides?: Record<string, JsonObject>;
}

export type ConnectionType = 'depends-on' | 'connects-to' | 'triggers';

/** Only on `triggers` connections. */
export interface ConnectionOptions {
  /** The function in the code that handles the trigger (`^[A-Za-z_][A-Za-z0-9_]{0,127}$`). */
  functionName?: string;
}

export interface BlueprintConnection {
  sourceId: string;
  targetId: string;
  type: ConnectionType;
  alias?: string;
  options?: ConnectionOptions;
}

export type SecretChangePolicy = 'wait' | 'restart';

export interface SecretDeclaration {
  description?: string;
  /** Nebisys generates the value once (16–128 characters). */
  generate?: { length: number };
  /** Default `wait`. */
  onChange?: SecretChangePolicy;
}

export interface BlueprintDocument {
  schemaVersion: 1;
  name: string;
  description?: string;
  parameters?: Record<string, ParameterDefinition>;
  secrets?: Record<string, SecretDeclaration>;
  resources: BlueprintResource[];
  connections?: BlueprintConnection[];
  /** Feature spec 17, phase 2: traffic to a resource of another blueprint, opened once a network admin approves it. Omitted when empty. */
  networkRequests?: NetworkRequestDefinition[];
}

/** Feature spec 17, phase 2 (spec 01 5.1). `alias` defaults to the id uppercased with `-` → `_`. */
export interface NetworkRequestDefinition {
  id: string;
  sourceId: string;
  target: { blueprintId: string; resourceId: string };
  alias?: string;
  reason: string;
}

export interface Position {
  x: number;
  y: number;
}

export interface Layout {
  positions: Record<string, Position>;
  ui?: JsonValue;
}

// ---- Blueprints (owner: 04 HTTP API) ----

export interface BlueprintSummary {
  id: string;
  name: string;
  description: string | null;
  latestVersion: number;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

/** A blueprint's newest deployments in the blueprints list (spec 4.4, at most 10, newest first). */
export interface DeploymentSummary {
  id: string;
  name: string;
  status: DeploymentStatus;
  drift: DriftState;
  activeRunId: string | null;
  currentBlueprintVersion: number | null;
}

/** An item of `GET /blueprints`: the summary with its deployments. */
export interface BlueprintListItem extends BlueprintSummary {
  deployments: DeploymentSummary[];
}

export interface Blueprint extends BlueprintSummary {
  content: BlueprintDocument;
  contentHash: string;
  layout: Layout;
  diagnostics: Diagnostic[];
}

export interface ValidationResult {
  valid: boolean;
  diagnostics: Diagnostic[];
}

export interface BlueprintVersionSummary {
  version: number;
  contentHash: string;
  createdAt: string;
  createdBy: string;
}

export interface BlueprintVersion extends BlueprintVersionSummary {
  blueprintId: string;
  content: BlueprintDocument;
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

export interface PageRequest {
  limit?: number;
  cursor?: string | null;
}

// ---- Deployments ----

export type OnFailure = 'stop' | 'rollback';

export interface DeploymentSettings {
  parameters: Record<string, ParameterValue>;
  defaultPlacement: Placement | null;
  placementOverrides: Record<string, Placement>;
  cloudAccounts: Record<string, string>;
  secretStore: string;
  onFailure: OnFailure;
  /** Vault placement, field by field over `defaultPlacement`. Optional. */
  vault?: Placement | null;
  /**
   * Feature spec 15: secrets in a managed vault on the vault provider, or from the secret store. Written by create;
   * null in settings stored before vault modes existed (`Deployment.effectiveVaultMode` then applies).
   */
  vaultMode?: VaultMode | null;
  /** Feature spec 17: the network the deployment attaches to; absent or null = standalone (or retained). */
  network?: NetworkSetting | null;
}

export type VaultMode = 'managed' | 'store';

export type DeploymentStatus = 'new' | 'active' | 'failed' | 'destroyed';
export type DriftState = 'unknown' | 'none' | 'detected';

export interface LastSuccessfulApply {
  runId: string;
  blueprintVersion: number;
  completedAt: string;
}

export interface Deployment {
  id: string;
  blueprintId: string;
  name: string;
  settings: DeploymentSettings;
  status: DeploymentStatus;
  drift: DriftState;
  needsRefresh: boolean;
  needsRecovery: boolean;
  currentBlueprintVersion: number | null;
  lastSuccessfulApply: LastSuccessfulApply | null;
  activeRunId: string | null;
  revision: number;
  /** Hash of the canonical settings (spec 4.4); equal settings have equal hashes; null never equals. */
  settingsHash: string | null;
  /** The recorded managed vault, kept after it was deleted (spec 4.4, A23); null when none is recorded. */
  vault?: DeploymentVault | null;
  /** A live vault is recorded, or a soft-deleted one until its `purgeAfter`: the vault placement can't change. */
  vaultPlacementFixed?: boolean;
  /** Feature spec 15: the stored settings' effective vault mode; null without a vault provider. */
  effectiveVaultMode?: VaultMode | null;
  /** Feature spec 15: the PATCH response's warnings (`VAULT_SECRETS_WILL_BE_DELETED`). */
  warnings?: ErrorInfo[];
  /**
   * Only in `GET /deployments/{id}`: the soft-deleted vault's re-read this request made, at the top level so it is
   * there also when it found the vault purged (`vault` is then null); null when nothing was due (code review C21).
   */
  vaultCheck?: DeletedVaultCheck | null;
  /** Feature spec 17: the deployment's network; absent or null when standalone. */
  network?: DeploymentNetwork | null;
  /** Feature spec 17: the runs a PATCH created (a detach: the detach run and the system apply); only then present. */
  runs?: RunSummary[];
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export type DeploymentVaultState = 'live' | 'soft-deleted';
export type DeletedVaultFinding = 'soft-deleted' | 'purged' | 'recovered' | 'unknown';

/** A re-read of a soft-deleted vault in the cloud (spec 4.4); `error`: couldn't check. */
export interface DeletedVaultCheck {
  checkedAt: string;
  finding: DeletedVaultFinding;
  error: ErrorInfo | null;
}

/** `Deployment.vault` (spec 4.4, A23). */
export interface DeploymentVault {
  provider: string;
  region: string;
  name: string;
  state: DeploymentVaultState;
  createdAt: string;
  deletedAt: string | null;
  purgeAfter: string | null;
  /** Only in `GET /deployments/{id}`: the re-read this request made; null when nothing was due. */
  check?: DeletedVaultCheck | null;
}

export type PlacementSource = 'override' | 'blueprint' | 'default' | 'none';

/** A resource's effective placement in the dry run (spec 4.4, A28). */
export interface ResourcePlacement {
  resourceId: string;
  type: string;
  provider: string | null;
  region: string | null;
  providerSource: PlacementSource;
  regionSource: PlacementSource;
  /** With `deploymentId`: the placement under the stored settings. */
  current: Placement | null;
  /** The placement of the last successful apply; null when not deployed. */
  applied: Placement | null;
  replaced: boolean;
  stateful: boolean;
  replaceImpacts: string[];
}

/** `POST /blueprints/{id}/deployments/validate` (spec 4.4, A28). */
export interface DeploymentValidation {
  valid: boolean;
  blueprintVersion: number;
  settings: DeploymentSettings;
  diagnostics: Diagnostic[];
  resources: ResourcePlacement[];
  vault: { provider: string; region: string; managed: boolean; placementFixed: boolean } | null;
  /** Feature spec 15: the PATCH's warnings, as it would give them. */
  warnings?: ErrorInfo[];
  /** Feature spec 15: the deletion an apply with these settings makes (existing deployment only). */
  vaultSecretsDeletion?: VaultSecretsDeletion | null;
  /** Feature spec 17: what the body's `settings.network` change does (null when it doesn't change the stored one). */
  networkImpact?: DeploymentNetworkImpact | null;
}

/** The deletion of a namespace vault's secrets an apply makes (feature spec 15; retention 0 = permanent). */
export interface VaultSecretsDeletion {
  count: number;
  systemCount: number;
  prefix: string;
  label: string;
  region: string;
  retentionDays: number;
  recoverableUntil: string | null;
}

/** An item of `GET /deployments` (all blueprints). */
export interface DeploymentListItem extends Deployment {
  blueprintName: string;
  blueprintLatestVersion: number;
}

/** Filters of `GET /deployments`; empty members don't filter. */
export interface DeploymentListFilter {
  blueprintId?: string | null;
  status?: DeploymentStatus | null;
  drift?: DriftState | null;
  provider?: string | null;
  q?: string | null;
}

export interface CreateDeploymentRequest {
  name: string;
  settings: Partial<DeploymentSettings>;
}

export type ResourceRunStatus = 'pending' | 'in-progress' | 'completed' | 'failed' | 'skipped';

export interface DeploymentResource {
  resourceId: string;
  type: string;
  provider: string;
  region: string;
  status: ResourceRunStatus;
  primaryCloudId: string | null;
  outputs: Record<string, JsonValue>;
  secretOutputs: string[];
  updatedAt: string;
}

// ---- Runs (formats owned by 05 Run worker) ----

export type RunKind = 'plan' | 'apply' | 'destroy' | 'refresh';
export type RunStatus = 'pending' | 'running' | 'completed' | 'failed' | 'canceled';
/** `attachment`: connect, detach and system runs; `request-decision`: decision runs (feature spec 17). */
export type TriggeredBy = 'user' | 'rollback' | 'attachment' | 'request-decision';
export type ChangeAction = 'create' | 'update' | 'replace' | 'delete' | 'no-change';

/** A changed child resource of a planned resource (spec 4.6), e.g. a role assignment. */
export interface ChildChange {
  pulumiType: string;
  name: string;
  action: ChangeAction;
}

export interface PlanChange {
  resourceId: string;
  type: string;
  action: ChangeAction;
  changedProperties: string[];
  /** Absent in plans stored before the redesign (read as [], false, []). */
  children?: ChildChange[];
  stateful?: boolean;
  replaceImpacts?: string[];
}

export interface SharedChange {
  provider: string;
  pulumiType: string;
  name: string;
  action: ChangeAction;
  /** `vault` for the managed vault's resources. */
  role?: string | null;
}

export type VariableChangeKind = 'added' | 'changed' | 'removed';

export interface VariableSide {
  value: string | null;
  expression: string | null;
  locationType: string;
  secretName: string | null;
}

/** One variable a plan changes (spec 4.4 Runs, A16). Never a secret value. */
export interface VariableChange {
  name: string;
  change: VariableChangeKind;
  secret: boolean;
  locationType: string;
  before: VariableSide | null;
  after: VariableSide | null;
}

export interface AppVariableChanges {
  resourceId: string;
  restarts: boolean;
  changes: VariableChange[];
}

/** `GET /runs/{id}/variable-changes`. */
export interface RunVariableChanges {
  runId: string;
  apps: AppVariableChanges[];
}

export interface DriftEntry {
  resourceId: string;
  change: ChangeAction;
  changedProperties: string[];
}

/** The secrets service's `SecretDriftEntry` (spec 14, 5.3). */
export interface SecretDriftEntry {
  name: string;
  kind: SecretKind;
  drift: SecretDriftKind[];
}

/** A setting changed in the cloud since the apply, found by a refresh (spec 4.6). Never a secret value. */
export interface RunSettingDriftEntry {
  resourceId: string;
  name: string;
  change: SettingDriftChange;
  secret: boolean;
  value: string | null;
}

export interface RunResult {
  diagnostics: Diagnostic[];
  plan?: { changes: PlanChange[]; sharedChanges: SharedChange[] } | null;
  drift?: DriftEntry[] | null;
  /** Refresh runs: secrets with drift. */
  secretDrift?: SecretDriftEntry[] | null;
  /** Refresh runs: app settings changed in the cloud since the apply (absent in older results). */
  settingsDrift?: RunSettingDriftEntry[] | null;
  /** Refresh runs: the apps whose settings were read and compared. */
  settingsChecked?: string[] | null;
  /** Completed full plans: the variable changes stored at completion. */
  variableChanges?: AppVariableChanges[] | null;
  stateMayBeStale?: boolean;
  /** Feature spec 15: full plans and applies that delete a namespace vault's secrets. */
  vaultSecretsDeletion?: VaultSecretsDeletion | null;
  /** Feature spec 17: plans of attached deployments: what the connect run after the apply will do (absent when it couldn't
   * be computed, warning `NETWORK_STEPS_UNAVAILABLE`). */
  networkSteps?: NetworkStep[] | null;
  /** Feature spec 17, phase 2: the request instances the version creates, changes or removes (spec 05 5.1). */
  networkRequests?: PlannedNetworkRequest[] | null;
  /** Feature spec 17: plans of attached deployments: the private connections the apply interrupts, per region. */
  interruptions?: RunInterruption[] | null;
  /** Feature spec 17: network plans: the impact of their settings against the last applied ones (null: never applied). */
  impact?: NetworkImpact | null;
}

/** What a connect run will do for a deployment (`componentKey`: `network.attachment/<id>/<provider>/<region>`, …). */
/** Feature spec 17, phase 2 (spec 05 5.1): a request instance in a deployment plan's network section. */
export interface PlannedNetworkRequest {
  networkId: string;
  networkName: string;
  requestId: string;
  sourceResourceId: string;
  provider: string;
  region: string;
  target: { deploymentId: string; name: string; resourceId: string } | null;
  port: number | null;
  change: 'new' | 'changed' | 'removed' | 'unchanged';
  appliedDecision: RequestDecision | null;
  proposedDecision: RequestDecision | null;
  enforcement: RequestEnforcement | null;
  enforcing: boolean;
  /** The apply closes open access (an approved shape replaced or removed). */
  closesOnApply: boolean;
  inactiveReason: string | null;
}

export interface NetworkStep {
  componentKey: string;
  type: string;
  action: 'create' | 'update' | 'replace' | 'delete';
  text: string;
}

export interface RunInterruption {
  provider: string;
  region: string;
  text: string;
}

/** `vault` applies only the deployment's vault (allowed for `apply`). `attachment`: network runs of one deployment's
 * components (feature spec 17). Default `full`. */
export type RunScope = 'full' | 'vault' | 'attachment';

export interface RunProgress {
  completed: number;
  total: number;
}

export interface Run {
  id: string;
  /** Null for network runs (feature spec 17, once the API returns them; check `target`). */
  deploymentId: string;
  kind: RunKind;
  /** Absent from older API versions; treat as `full`. */
  scope?: RunScope;
  status: RunStatus;
  triggeredBy: TriggeredBy;
  parentRunId: string | null;
  blueprintVersion: number | null;
  expectedPlanRunId: string | null;
  onFailure: OnFailure;
  cancelRequested: boolean;
  settings: DeploymentSettings;
  progress: RunProgress | null;
  result: RunResult | null;
  error: { code: string; message: string } | null;
  /** Hash of the settings snapshot, comparable with `Deployment.settingsHash`. */
  settingsHash: string | null;
  /** Feature spec 17: what the run acts on. Absent from older API versions: a deployment run. */
  target?: RunTarget;
  networkId?: string | null;
  /** Connect and detach runs: the deployment whose components they apply. */
  attachmentDeploymentId?: string | null;
  /** Attachment-scope network runs: what the run does for that deployment (`decision`: phase 2). */
  attachmentMode?: 'connect' | 'detach' | 'decision' | null;
  /** The run is claimable only once this run completed. */
  waitForRunId?: string | null;
  /** Pending runs: why the run waits (computed on read). */
  waitingFor?: WaitingFor | null;
  /** Deployment runs: counts of the spoke group's changes (the plan's network chips). */
  networkChanges?: RunChanges | null;
  createdAt: string;
  createdBy: string;
  startedAt: string | null;
  finishedAt: string | null;
}

/** Counts of the plan's resource changes per action, stored when the run completed. */
export interface RunChanges {
  create: number;
  update: number;
  replace: number;
  delete: number;
}

export interface RunWarning {
  code: string;
  resourceId: string | null;
}

/** A run without settings and result, for lists (`GET /runs`, `GET /deployments/{id}/runs?view=summary`). */
export interface RunSummary extends Omit<Run, 'settings' | 'result'> {
  deploymentName: string;
  blueprintId: string;
  blueprintName: string;
  /** Null: no plan (refresh, destroy, failed before the preview, not finished) or not recorded. */
  changes: RunChanges | null;
  /** Null until the run finished, or not recorded. */
  warnings: RunWarning[] | null;
  /** Feature spec 17: network runs (deploymentName, blueprintId and blueprintName are null for them). */
  networkName?: string | null;
  /** Connect and detach runs: the deployment's name ("Connect orders-prod"). */
  attachmentDeploymentName?: string | null;
}

/** `status` filter values of the run lists: the run statuses plus `active` and `finished`. */
export type RunStatusFilter = RunStatus | 'active' | 'finished';

/** Filters of `GET /runs` and of a deployment's runs; empty members don't filter. */
export interface RunListFilter {
  status?: RunStatusFilter | null;
  kind?: RunKind | null;
  deploymentId?: string | null;
  /** Feature spec 17: `deployment` or `network` runs only. */
  target?: 'deployment' | 'network' | null;
  /** Feature spec 17: the runs of a network, its attachment runs included. */
  networkId?: string | null;
}

export interface CreateRunRequest {
  kind: RunKind;
  blueprintVersion?: number;
  expectedPlanRunId?: string;
  onFailure?: OnFailure;
  scope?: RunScope;
}

export type EventLevel = 'debug' | 'info' | 'warning' | 'error';
export type EventKind = 'run-status' | 'resource-status' | 'progress' | 'log';

export interface RunEvent {
  sequence: number;
  timestamp: string;
  level: EventLevel;
  kind: EventKind;
  resourceId: string | null;
  message: string;
  data: JsonObject | null;
}

export interface RunEventPage {
  items: RunEvent[];
  nextAfter: number;
}

// ---- Catalog and providers ----

export type OutputType = 'string' | 'number' | 'boolean' | 'string-list';

export interface ResourceTypeOutput {
  name: string;
  type: OutputType;
  secret: boolean;
  description: string;
}

export interface WiringKey {
  key: string;
  fromOutput: string;
  secret: boolean;
}

export type WiringConnectionType = 'connects-to' | 'triggers';

export interface WiringRule {
  sourceType?: string;
  targetType: string;
  /** Absent means `connects-to`. */
  connectionType?: WiringConnectionType;
  /** Empty for `triggers` rules: their values are provider-defined. */
  keys: WiringKey[];
  access: 'none' | 'network' | 'identity';
  privateScope: 'none' | 'same-provider' | 'same-provider-and-region';
  publicFallback: 'none' | 'always' | 'requires-allowlist';
  /** `triggers` only: the connection must set `options.functionName`. */
  requiresFunctionName?: boolean;
  /** The source's property at `pointer` must be one of `values` (feature spec 16, phase 3a). Absent = none. */
  sourceRequires?: SourceRequirement | null;
}

export interface SourceRequirement {
  /** JSON Pointer into the source's `properties`, for example `/engine`. */
  pointer: string;
  values: string[];
  message: string;
}

export interface ResourceType {
  type: string;
  schemaVersion: number;
  displayName: string;
  category: string;
  description: string;
  icon: string;
  propertiesSchema: JsonSchema;
  outputs: ResourceTypeOutput[];
  injectionProperty: string | null;
  /** Replacing or deleting it loses data (`data.sql`, `storage.bucket`). */
  stateful?: boolean;
  /** Cloud-neutral consequences of a replacement (`data-deleted`, `messages-lost`, `code-redeploy`). */
  replaceImpacts?: string[];
  /** Feature spec 16 (3d): JSON Pointer of the type's external triggers property; null or absent = none. */
  externalTriggersProperty?: string | null;
  /** Feature spec 17, phase 2: a network request of another blueprint may target the type. */
  privatelyReachable?: boolean;
  /** Feature spec 17, phase 2: the port a request to it opens (TCP): fixed, or by the value of a property. */
  requestPort?: {
    port: number | null;
    property: string | null;
    byValue: Record<string, number> | null;
  } | null;
  providers: string[];
  connections: WiringRule[];
}

export interface Region {
  name: string;
  displayName: string;
  geography: string;
}

export interface Provider {
  key: string;
  displayName: string;
  supportedTypes: string[];
  regions: Region[];
  overrideSchemas: Record<string, JsonSchema>;
  /** The provider's connector creates a managed vault for secrets. Absent means false. */
  supportsManagedVault?: boolean;
  /** Feature spec 15: what the provider's managed vault can't deliver (a blueprint using it keeps the store). */
  managedVaultLimitations?: ManagedVaultLimitation[];
  /** What a triggered function gets, per source type (feature spec 16, 4.3). Absent means []. */
  triggerSources?: TriggerSourceInfo[];
  /** What a `connects-to` source gets, per target type. Absent means []. */
  connectionTargets?: ConnectionTargetInfo[];
  /** The native service a type becomes. Absent means []. */
  serviceNames?: ServiceNameInfo[];
}

/**
 * Applies when the blueprint resource's value at `pointer` (`/properties/…` or `/providerOverrides/<key>/…`)
 * is the literal `value` (non-strings by their JSON text).
 */
export interface ResourceCondition {
  pointer: string;
  value: string;
}

export interface TriggerSettingInfo {
  /** `__fullyQualifiedNamespace`, `_EVENTHUB`: the function receives `<Alias><suffix>`. */
  suffix: string;
  description: string;
  /** The value differs per function app (a subscription or consumer group of its own). */
  perFunction: boolean;
}

export interface TriggerSourceInfo {
  sourceType: string;
  /** Null for the default entry; entries with a condition come first. */
  when?: ResourceCondition | null;
  /** Role names the function's identity gets; [] when none. */
  roles: string[];
  settings: TriggerSettingInfo[];
  /** The subscription or upstream is created only once the function's code is deployed. */
  waitsForCode: boolean;
  note?: string | null;
  /** A step only the user can take. */
  action?: string | null;
  /** Copyable text for the action. */
  actionSnippet?: string | null;
  /** What the trigger changes on the function app itself. */
  functionEffect?: string | null;
  /** Runtime → binding example with `{Alias}` as placeholder. */
  examples?: Record<string, string> | null;
}

export interface ConnectionTargetInfo {
  targetType: string;
  when?: ResourceCondition | null;
  /** Role names the source's identity gets on the target; [] when none. */
  roles: string[];
  /** How the app signs in. */
  note?: string | null;
}

export interface ServiceNameInfo {
  type: string;
  when?: ResourceCondition | null;
  name: string;
}

export interface ManagedVaultLimitation {
  type: string;
  pointer: string;
  code: string;
  message: string;
}

export interface SecretStore {
  name: string;
  kind: string;
  isDefault: boolean;
}

// ---- Secrets (owner: 14 Secrets service, returned by the API as they are) ----

export type SecretKind = 'declared' | 'system' | 'unmanaged';

export type SecretDriftKind =
  | 'changed-outside'
  | 'deleted-outside'
  | 'disabled'
  | 'expired'
  | 'tags-edited'
  | 'unmanaged'
  | 'missing-value'
  | 'rotated'
  | 'rotation-conflict';

/** `setting` is a key of the injection map, or outside it the JSON Pointer of the property (`/registry/password`). */
export interface SecretUsage {
  resourceId: string;
  setting: string;
}

/** Secret metadata; values are never returned. */
export interface SecretMetadata {
  name: string;
  kind: SecretKind;
  description: string | null;
  /** Declared: the declaration's; system: `wait`; unmanaged: null. */
  onChange: SecretChangePolicy | null;
  /** Declared with `generate`. */
  generated: boolean;
  /** In the vault, not deleted, enabled, with a current version; null when unknown (store mode, no listing). */
  hasValue: boolean | null;
  currentVersion: string | null;
  updatedAt: string | null;
  /** Who wrote the recorded current version; null when it isn't recorded. */
  updatedBy: string | null;
  enabled: boolean;
  expiresAt: string | null;
  /** All tags of the current version. */
  tags: Record<string, string>;
  usedBy: SecretUsage[];
  /** In enum order. */
  drift: SecretDriftKind[];
  /** An unmanaged secret a user ignored (it then has no drift). */
  ignored: boolean;
  /** A soft-deleted secret: when it was deleted. */
  deletedAt?: string | null;
  /** A soft-deleted secret: until when it can be recovered. */
  recoverableUntil?: string | null;
  /** With drift `deleted-outside` only. */
  recoverability?: Recoverability | null;
  /** The newest version Nebisys wrote or accepted. */
  lastRecorded?: RecordedVersion | null;
  /** Where the secret lives; null for vault status `none`. */
  location?: SettingLocation | null;
  /** Feature spec 15: an entry with the declared spelling in another case exists in the vault (its native name). */
  nameConflict?: string | null;
  /** Feature spec 15: an unmanaged entry tagged with this other deployment's ID. */
  foreignDeployment?: string | null;
}

export type Recoverability = 'recoverable' | 'not-recoverable' | 'unknown';

export interface RecordedVersion {
  versionId: string;
  writtenBy: string;
  createdOn: string;
  accepted: boolean;
}

/** A failed vault listing or a design that doesn't resolve. */
export interface ErrorInfo {
  code: string;
  message: string;
}

export interface SecretStoreRef {
  name: string;
  kind: string;
  label: string;
}

/**
 * `ready`: a vault is recorded. Otherwise from the design: `not-created` (managed vault, not created yet:
 * "Prepare vault"), `store` (the store fallback: values come from the secret store), `none` (no secrets used).
 */
export type VaultStatus = 'none' | 'store' | 'not-created' | 'ready';

export interface VaultOverview {
  status: VaultStatus;
  managed: boolean;
  provider: string | null;
  region: string | null;
  name: string | null;
  uri: string | null;
  createdAt: string | null;
  /** Location type of the deployment's secrets (`azure-key-vault`, `store:<kind>`); null for `none`. */
  type?: string | null;
  label?: string | null;
  consoleUrl?: string | null;
  /** Status `store`: the configured store. */
  store?: SecretStoreRef | null;
  listedAt?: string | null;
  stale?: boolean;
  listingError?: ErrorInfo | null;
  /** The vault placement is fixed (mirrors `Deployment.vaultPlacementFixed`). */
  placementFixed?: boolean;
  /** A soft-deleted, still fixed vault: its purge date. */
  deletedUntil?: string | null;
  deletedCheck?: DeletedVaultCheck | null;
  /** Status `ready` (opt-in reveal): the cloud account a reveal reads with; null otherwise. */
  readIdentity?: { cloudAccountName: string } | null;
  /** Feature spec 15: the provider's retention of deleted secrets in days (0 = deleted at once). */
  deletedRetentionDays?: number | null;
  /** Feature spec 15: a vault-mode switch the next apply completes. */
  modeChange?: 'to-managed' | 'to-store' | null;
  /** Feature spec 15: the vault's secrets outlive it (namespace vaults); a destroy deletes them inside the run. */
  outlivesVault?: boolean;
}

/** Counts of secrets with `missing-value`, `changed-outside` and `unmanaged`. */
export interface SecretsSummary {
  missingValues: number;
  changedOutside: number;
  unmanaged: number;
}

/** `GET /deployments/{id}/secrets`; also the result of rewrite-tags. */
export interface SecretsOverview {
  vault: VaultOverview;
  secrets: SecretMetadata[];
  summary: SecretsSummary;
}

export interface SetSecretRequest {
  value: string;
  /** null: follow the secret's `onChange` policy. */
  restart: boolean | null;
}

export type RevealPurpose = 'view' | 'copy';

/** `POST /deployments/{id}/secrets/{name}/reveal` (opt-in reveal, spec 4.4 "Reveal"). */
export interface RevealRequest {
  purpose: RevealPurpose;
  /** Trimmed; null when empty. */
  reason: string | null;
}

/**
 * The reveal's 200 response. It holds a secret value: it never enters the query cache, storage, logs or
 * error reports, and lives only in the state of the component that shows it (spec 6.10.18).
 */
export interface RevealedSecret {
  name: string;
  version: string;
  /** False: the version was written outside Nebisys. */
  recorded: boolean;
  updatedAt: string | null;
  value: string;
  /** The `secret-revealed` request entry written before the read. */
  auditEventId: string;
}

/** A restart after a value change. A failed restart doesn't fail the call. */
export interface AppRestart {
  resourceId: string;
  succeeded: boolean;
  error: string | null;
}

/** Result of `PUT /deployments/{id}/secrets/{name}`. */
export interface SecretValueSetResult {
  secret: SecretMetadata;
  restarts: AppRestart[];
  /** Feature spec 15: `SECRET_RECREATED`. */
  warnings?: ErrorInfo[];
}

/** Result of `POST /deployments/{id}/resources/{resourceId}/restart` (feature spec 15). */
export interface AppRestartResult {
  resourceId: string;
  succeeded: boolean;
  error: string | null;
  action: NowActionKind;
  appReferenceStatus: SettingReferenceStatus | null;
}

/** What a platform's rollout does: App Service restarts, ECS rolls out new tasks, Cloud Run deploys a new revision. */
export type NowActionKind = 'restart' | 'roll-out' | 'new-revision';

/** A rollout through Nebisys, remembered by the server (feature spec 15). */
export interface AppRestartInfo {
  at: string;
  by: string;
  action: NowActionKind;
}

/** Unknown values count as `plain`. */
export type SettingSource = 'plain' | 'secret' | 'system-secret' | 'connection' | 'trigger' | 'platform';
/** `network-request`: a network request's `<ALIAS>_HOST` and `_PORT` (feature spec 17, phase 2). */
export type SettingOrigin =
  'settings' | 'property' | 'connection' | 'trigger' | 'platform' | 'network-request';
export type SettingKind = 'declared' | 'system' | 'generated';
export type SyncState = 'in-sync' | 'changed' | 'added' | 'removed' | 'not-applied';
export type NeutralReferenceStatus =
  'pending' | 'resolved' | 'invalid' | 'access-denied' | 'not-found' | 'unreachable' | 'unknown';

/** Status of a vault reference: the platform's raw value and its neutral meaning. */
export interface SettingReferenceStatus {
  status: string;
  details: string | null;
  neutral: NeutralReferenceStatus;
  /** Feature spec 15: per-app statuses: when the app's oldest task (or serving revision) read its secrets. */
  readAt?: string | null;
}

export type SetValueCapability = 'vault' | 'design' | 'deployment' | 'outside' | 'none';

/** Role-free: the UI combines them with `/me` permissions. */
export interface LocationCapabilities {
  reveal: boolean;
  setValue: SetValueCapability;
  drift: boolean;
  referenceStatus: boolean;
  versions: boolean;
  recover: boolean;
}

/** Where a value lives (spec 4.4, Variables). `type` is an open set: unknown types use the generic fallback. */
export interface SettingLocation {
  type: string;
  label: string;
  container: string | null;
  name: string | null;
  nativeId: string | null;
  account: string | null;
  region: string | null;
  versionSelector: string | null;
  consoleUrl: string | null;
  secret: boolean;
  capabilities: LocationCapabilities;
  limits: LocationLimits | null;
  softDelete: { days: number } | null;
  /** Feature spec 15: the vault rotates this secret (AWS) or notifies about it (GCP). */
  rotation: RotationInfo | null;
}

export interface LocationLimits {
  maxValueBytes: number;
  writeAdvice: string | null;
  /** Feature spec 15 (AWS 600): the vault advises at most one write per this many seconds. */
  minWriteIntervalSeconds?: number | null;
  /** Feature spec 15 (GCP 2 and 7). */
  versionsKept?: number | null;
  versionDestroyDelayDays?: number | null;
  /** Feature spec 15 (AWS): a secret created again gets a new ID and needs an apply. */
  recreateNeedsApply?: boolean;
}

export interface RotationInfo {
  enabled: boolean;
  lastRotatedAt: string | null;
  nextRotationAt: string | null;
  /** GCP: rotation only sends notifications. */
  notifies?: boolean;
}

export type DeliveryMode = 'reference' | 'plain' | 'runtime-read';

/** How the app gets a value: facts recorded at the apply plus the platform's delivery profile. */
export interface SettingDelivery {
  platform: string | null;
  mode: DeliveryMode;
  label: string;
  envName: string | null;
  exposedAsEnv: boolean;
  secret: boolean;
  recorded: boolean;
  pickup: string;
  nowAction: {
    label: string;
    disruption: string;
    default: 'from-on-change' | 'on';
    /** Feature spec 15: what the action does, and whether the UI confirms it. */
    action?: NowActionKind;
    confirm?: boolean;
  } | null;
  referenceStatus: 'per-variable' | 'per-app' | 'none';
  refreshReferences: boolean;
  readersText: string | null;
  settingsChangeRestarts: boolean;
}

export interface OutputRef {
  resourceId: string;
  output: string;
}

export interface DesignSide {
  expression: string | null;
  resolvedValue: string | null;
  locationType: string;
}

export type SettingDriftChange = 'changed' | 'added' | 'removed';

/** One setting of an app that changed in the cloud since the apply (`settingsDrift.entries`). */
export interface SettingDriftItem {
  name: string;
  change: SettingDriftChange;
  secret: boolean;
  /** The cloud's value of a changed non-secret setting, or a changed reference's text; null otherwise. */
  value: string | null;
}

/** An app's settings drift ("Changed in the cloud"), from the newest refresh after the last apply. */
export interface AppSettingsDrift {
  runId: string;
  checkedAt: string;
  entries: SettingDriftItem[];
}

/** A row's own entry of its app's settings drift. */
export interface SettingCloudDrift {
  change: 'changed' | 'removed';
  value: string | null;
}

/** One variable of an app (`SettingRow`, spec 4.4). Secret rows never carry a value. */
export interface ResourceSetting {
  /** Injection map key, the JSON Pointer of a secret property (`/registry/password`), `<ALIAS>_<KEY>`, or a setting name. */
  name: string;
  source: SettingSource;
  value: string | null;
  secretName: string | null;
  referenceStatus: SettingReferenceStatus | null;
  /** The drift of the setting's secret. */
  drift: SecretDriftKind[];
  origin: SettingOrigin;
  kind: SettingKind | null;
  expression: string | null;
  parameter: string | null;
  output: OutputRef | null;
  resolvedValue: string | null;
  location: SettingLocation;
  delivery: SettingDelivery;
  sync: SyncState | null;
  design: DesignSide | null;
  pickupPendingUntil: string | null;
  /** Changed or removed in the cloud since the apply (absent from older servers). */
  cloudDrift?: SettingCloudDrift | null;
  /** Feature spec 15: a per-app app read its references before the secret's current version was written. */
  olderVersionInUse?: boolean;
  /** Feature spec 16 (3d): the external trigger that reads this setting (the first listing entry). */
  externalTrigger?: SettingExternalTrigger | null;
  /** Feature spec 17, phase 2: a network request's row (location type `network-request`, spec 04 6.10). */
  request?: SettingNetworkRequest | null;
}

/** Feature spec 17, phase 2. `issue`: `NETWORK_NOT_ATTACHED`, `NETWORK_REQUEST_TARGET_NOT_ATTACHED`,
 * `NETWORK_REQUEST_TARGET_AMBIGUOUS`, `NETWORK_REQUEST_PENDING` or null. */
export interface SettingNetworkRequest {
  requestId: string;
  networkId: string | null;
  networkName: string | null;
  target: { deploymentId: string; name: string } | null;
  state: string;
  issue: string | null;
}

/** An external trigger that reads a setting: `{ name: 'Payments', kind: 'kafka' }`. */
export interface SettingExternalTrigger {
  name: string;
  kind: string;
}

/** `GET /deployments/{id}/resources/{resourceId}/settings` (404 `RESOURCE_NOT_FOUND` before the resource is applied). */
export interface ResourceSettings {
  resourceId: string;
  type: string;
  settings: ResourceSetting[];
  /** Metadata of every secret the settings use, in first-use order. */
  secrets: SecretMetadata[];
  /** Set when the reference statuses couldn't be read (statuses are then null). */
  referenceStatusError: string | null;
  provider?: string | null;
  region?: string | null;
  applied?: boolean;
  inDesign?: boolean;
  settingsRecorded?: boolean;
  platform?: string | null;
  referenceStatusCheckedAt?: string | null;
  listedAt?: string | null;
  stale?: boolean;
  listingError?: ErrorInfo | null;
  settingsDrift?: AppSettingsDrift | null;
  /** Feature spec 15: the app's own reference status (per-app platforms) and its last rollout. */
  appReferenceStatus?: SettingReferenceStatus | null;
  lastRestart?: AppRestartInfo | null;
}

/** One app's variables in `DeploymentVariables`. */
export interface AppVariables {
  resourceId: string;
  type: string;
  provider: string | null;
  region: string | null;
  applied: boolean;
  inDesign: boolean;
  settingsRecorded: boolean;
  platform: string | null;
  settings: ResourceSetting[];
  referenceStatusCheckedAt: string | null;
  referenceStatusError: string | null;
  /** "Changed in the cloud": null when no refresh after the last apply checked the app. */
  settingsDrift?: AppSettingsDrift | null;
  /** Feature spec 15: the app's own reference status (per-app platforms: ECS, Cloud Run) and its last rollout. */
  appReferenceStatus?: SettingReferenceStatus | null;
  lastRestart?: AppRestartInfo | null;
}

/** `GET /deployments/{id}/settings`: every app's variables (spec 4.4, Variables). */
export interface DeploymentVariables {
  deploymentId: string;
  appliedVersion: number | null;
  designVersion: number | null;
  designError: ErrorInfo | null;
  vault: VaultOverview;
  listedAt: string | null;
  stale: boolean;
  listingError: ErrorInfo | null;
  apps: AppVariables[];
  secrets: SecretMetadata[];
}

// ---- Activity (spec 4.4 "Activity", owner 04 6.11) ----

/** Activity kinds; an open set: clients show unknown kinds by name. */
export const ACTIVITY_KINDS = [
  'deployment-created',
  'settings-changed',
  'deployment-deleted',
  'run-created',
  'run-completed',
  'secret-set',
  'secret-generated',
  'secret-accepted',
  'secret-ignored',
  'tags-rewritten',
  'secret-recovered',
  'references-refreshed',
  'app-restarted',
  'secret-changed-outside',
  'secret-revealed',
  'secret-rotated',
  'vault-secrets-deleted',
  'vault-secrets-restored',
] as const;
export type ActivityKind = (typeof ACTIVITY_KINDS)[number];
export type ActivityOutcome = 'succeeded' | 'failed' | 'canceled' | 'pending';

/** One entry of a deployment's activity (the audit log). Never holds a secret value. */
export interface ActivityEntry {
  id: string;
  at: string;
  deploymentId: string;
  deploymentName: string;
  actorId: string | null;
  actorName: string;
  /** An `ActivityKind`, or a kind this client doesn't know. */
  kind: string;
  secretName: string | null;
  resourceId: string | null;
  runId: string | null;
  versionId: string | null;
  outcome: ActivityOutcome;
  details: JsonObject;
  /** Feature spec 17: network rows (network activity, and attachment rows on both activities). */
  networkId?: string | null;
  networkName?: string | null;
}

/** Filters of `GET /deployments/{id}/activity`; empty members don't filter. */
export interface ActivityFilter {
  /** One or more kinds (sent comma-separated). */
  kind?: readonly string[] | null;
  secretName?: string | null;
  resourceId?: string | null;
  actor?: string | null;
}

// ---- Cloud accounts (fields owned by 07) ----

/** `unknown` is "couldn't check": only the permissions check has it, and it never fails a verification (spec 4.4). */
export type VerificationCheckStatus = 'passed' | 'failed' | 'unknown';

export interface VerificationCheck {
  name: string;
  passed: boolean;
  message: string;
  /** Missing in results stored before the redesign: read it from `passed`. */
  status?: VerificationCheckStatus;
}

/** Which credential signed in (for `ambient`, which one of the host's default chain). */
export interface CredentialSource {
  kind: string;
  description: string;
}

export interface VerificationResult {
  succeeded: boolean;
  identity: string | null;
  checks: VerificationCheck[];
  checkedAt: string;
  credentialSource?: CredentialSource | null;
}

export type CloudAccountUsageKind = 'settings' | 'last-apply';

/** A deployment that uses a cloud account (A9). Either usage blocks deletion. */
export interface CloudAccountUsage {
  deploymentId: string;
  deploymentName: string;
  blueprintId: string;
  blueprintName: string;
  status: DeploymentStatus;
  usages: CloudAccountUsageKind[];
}

export type CloudAccountScope = Record<string, string>;
export type CloudAccountAuth = { method: string } & Record<string, JsonValue>;

export interface CloudAccount {
  id: string;
  name: string;
  provider: string;
  scope: CloudAccountScope;
  auth: CloudAccountAuth;
  revision: number;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  lastVerifiedAt: string | null;
  lastVerification: VerificationResult | null;
  usedBy: CloudAccountUsage[];
  /** Feature spec 17: the networks that use the account. */
  usedByNetworks?: CloudAccountNetworkUsage[];
}

/** Feature spec 17: a network that uses a cloud account, as its connectivity account and/or an allowed account. */
export interface CloudAccountNetworkUsage {
  networkId: string;
  networkName: string;
  usages: ('connectivity' | 'allowed')[];
}

export interface CreateCloudAccountRequest {
  name: string;
  provider: string;
  scope: CloudAccountScope;
  auth: CloudAccountAuth;
}

export interface UpdateCloudAccountRequest {
  name: string;
  scope: CloudAccountScope;
  auth: CloudAccountAuth;
}

// ---- Shell ----

/** `network-admin`: feature spec 17, implied by `admin`. */
export type Role = 'reader' | 'designer' | 'operator' | 'admin' | 'network-admin' | 'secret-reader';

/** What `/me` says the caller may do, computed by the API's authorization policies (spec 4.4). */
/** `manage-networks`: feature spec 17, listed only while networking is enabled. */
export type Permission = 'read' | 'design' | 'operate' | 'admin' | 'manage-networks' | 'reveal-secrets';

export type AuthConfig =
  { mode: 'none' } | { mode: 'jwt'; authority: string; clientId: string; scopes: string[] };

export interface AppConfig {
  version: string;
  auth: AuthConfig;
  /** Feature spec 17: the Networking section exists only when `enabled`. Absent from older API versions: off. */
  networking?: NetworkingConfig;
}

export interface NetworkingConfig {
  enabled: boolean;
  /** Providers whose networking gates are on (`azure`, `aws`, `gcp`, `cloudflare`). */
  providers: string[];
  /**
   * Feature spec 17, phase 2: `Networking:Requests:RequireDifferentApprover` (absent from older API versions: the UI learns it
   * from the first `DECISION_BY_REQUESTER`).
   */
  requireDifferentApprover?: boolean;
}

export interface Me {
  /** Stable hash of the token's issuer and subject, for keying preferences; null without a subject. */
  id: string | null;
  name: string;
  roles: Role[];
  permissions: Permission[];
}

/** `GET /version` (spec 04, 6.13). */
export interface VersionInfo {
  version: string;
  /** `0.6.0+abc1234`, or the version alone when the build had no commit. */
  informationalVersion: string;
  commit: string | null;
}

export type ChangelogCategory = 'added' | 'changed' | 'deprecated' | 'removed' | 'fixed' | 'security';

export interface ChangelogEntry {
  /** Plain text with inline Markdown limited to `code` and **strong**. */
  text: string;
  breaking: boolean;
}

export interface ChangelogSection {
  category: ChangelogCategory;
  entries: ChangelogEntry[];
}

export interface ChangelogRelease {
  version: string;
  /** `yyyy-MM-dd`. */
  date: string;
  sections: ChangelogSection[];
}

/** `GET /version/changelog` (spec 04, 6.13): newest release first. */
export interface Changelog {
  unreleased: { sections: ChangelogSection[] };
  releases: ChangelogRelease[];
}

// ---- Networking (feature spec 17; spec 04 6.14, spec 18) ----

export type NetworkStatus = 'new' | 'active' | 'failed' | 'destroyed';
export type AttachPolicy = 'operators' | 'network-admins';
export type TopologyKind = 'isolated' | 'mesh' | 'hub-spoke' | 'micro-segmentation';
export type SpokeToSpoke = 'via-hub' | 'blocked';
export type CrossRegion = 'connected' | 'separate';
export type AttachmentSize = 'small' | 'medium' | 'large' | 'xlarge';
export type NetworkProvider = 'azure' | 'aws' | 'gcp' | 'cloudflare';
export type AllocationKind = 'region' | 'hub' | 'attachment';
export type AllocationState = 'allocated' | 'retained' | 'released';
export type AttachmentState =
  'pending' | 'spoke-ready' | 'connecting' | 'connected' | 'detaching' | 'detached' | 'failed';

/** A deployment's `settings.network` (spec 01). `sizes` keys are `<provider>/<region>`; `requestTargets` from phase 2. */
export interface NetworkSetting {
  networkId: string;
  size?: AttachmentSize | null;
  sizes?: Record<string, AttachmentSize> | null;
  requestTargets?: Record<string, string> | null;
}

/** `NetworkSettings` (spec 18 5.1), the JSON of `Network.settings`. */
export interface NetworkSettings {
  schemaVersion: 1;
  description?: string | null;
  attachPolicy: AttachPolicy;
  topology: { kind: TopologyKind; spokeToSpoke?: SpokeToSpoke | null; crossRegion: CrossRegion };
  addressPlan: {
    supernets: string[];
    reserved: { cidr: string; label: string }[];
    regionPrefixLength: number;
    hubPrefixLength: number;
    defaultAttachmentSize: AttachmentSize;
  };
  /** Keys: `NetworkProvider`. */
  providers: Partial<Record<NetworkProvider, ProviderNetworkSettings>>;
  dns: { internalZone?: string | null; privateLinkZones: 'auto' | 'all' };
  /** Entry points (spec 18 5.1); none can be added before phase 3. */
  entryPoints: EntryPointSettings[];
}

/** Feature spec 17, phase 3 (spec 18 5.1, 6.16): how an origin app accepts only its edge, strongest first. */
export type OriginAccessMode =
  | 'private-link'
  | 'private-endpoint'
  | 'vpc-origin'
  | 'serverless-neg'
  | 'access-restriction'
  | 'allow-list-and-header'
  | 'header'
  | 'none';

/** Feature spec 17, phase 3: an entry point of `NetworkSettings.entryPoints` (spec 18 5.1). */
export interface EntryPointSettings {
  id: string;
  label?: string | null;
  kind: string;
  tier?: string | null;
  /** Regional kinds (`azure-app-gateway`) only. */
  region?: string | null;
  /** Add-on ID -> settings; an absent add-on takes its registry `default`. */
  addOns?: Record<string, EntryPointAddOnSettings>;
  domains?: EntryPointDomain[];
  routes?: EntryPointRoute[];
}

export interface EntryPointAddOnSettings {
  enabled: boolean;
  /** `waf` only. */
  mode?: 'prevention' | 'detection' | null;
  managedRules?: boolean | null;
  botProtection?: boolean | null;
  /** 0 turns the rate limit off; absent: 2,000 per client IP and minute. */
  rateLimitPerMinute?: number | null;
}

export interface EntryPointDomain {
  hostname: string;
  certificate: { kind: 'managed' | 'key-vault'; secretId?: string | null };
}

export interface EntryPointRoute {
  id: string;
  /** Domains of the entry point; empty: all of them (or the edge's own host name without domains). */
  hostnames?: string[];
  /** Exact paths or prefixes ending in `/*`; default `["/*"]`. */
  paths?: string[];
  origin: { deploymentId: string; resourceId: string };
  /** `auto` (the default) or a mode. */
  originAccess?: 'auto' | OriginAccessMode;
  allowPlatformPush?: boolean;
}

export interface ProviderNetworkSettings {
  regions: string[];
  allowedAccounts: string[];
  firewall?: {
    enabled: boolean;
    tier?: string | null;
    egress?: 'direct' | 'through-firewall';
    allowedEgress?: string[];
  } | null;
  /** AWS only. */
  egress?: 'nat-per-attachment' | 'public-subnets' | null;
}

export interface RunTarget {
  kind: 'deployment' | 'network';
  id: string;
  name: string;
}

/** A short reference to a run (network lists and headers). */
export interface RunRef {
  id: string;
  kind: RunKind;
  status: RunStatus;
  triggeredBy: TriggeredBy;
  createdBy: string;
  createdAt: string;
}

export type WaitingReason =
  'network-run' | 'deployment-run' | 'awaited-run' | 'not-before' | 'networking-disabled';

/** Why a pending run waits (computed on read). `until`: the scheduled retry of `not-before`. */
export interface WaitingFor {
  reason: WaitingReason;
  runId: string | null;
  runKind: RunKind | null;
  target: RunTarget | null;
  by: string | null;
  since: string | null;
  until: string | null;
}

export interface NetworkLastApply {
  runId: string;
  completedAt: string;
  settingsHash: string | null;
}

export interface AttachmentCounts {
  connected: number;
  pending: number;
  connecting: number;
  waitingForApply: number;
  failed: number;
  retained: number;
}

export interface RequestCounts {
  pending: number;
  stale: number;
  /** Feature spec 17, phase 2: the topology enforces requests (otherwise the counts show neutral, "not enforced"). */
  enforcing?: boolean;
}

/** `GET /networks` items. */
export interface NetworkListItem {
  id: string;
  name: string;
  status: NetworkStatus;
  drift: DriftState;
  topology: TopologyKind;
  attachPolicy: AttachPolicy;
  providers: { provider: NetworkProvider; regions: string[] }[];
  attachments: AttachmentCounts;
  requests: RequestCounts;
  entryPoints: number;
  lastSuccessfulApply: NetworkLastApply | null;
  activeRun: RunRef | null;
  queuedRuns: number;
}

/** `GET /networks/{id}` (with `ETag`), `POST /networks`, `PATCH /networks/{id}`. */
export interface Network {
  id: string;
  name: string;
  status: NetworkStatus;
  drift: DriftState;
  needsRefresh: boolean;
  needsRecovery: boolean;
  settings: NetworkSettings;
  settingsHash: string;
  appliedSettingsHash: string | null;
  settingsChangedSinceApply: boolean;
  /** Provider key -> connectivity cloud account ID. */
  cloudAccounts: Record<string, string>;
  /** Recorded network outputs (spec 18 13.2): `azure/principal`, `azure/tenant`, `azure/zones`, … Never secrets. */
  outputs: JsonObject;
  attachments: AttachmentCounts;
  requests: RequestCounts;
  lastSuccessfulApply: NetworkLastApply | null;
  activeRun: RunRef | null;
  queuedRuns: number;
  blockedDeploymentApplies: number;
  /** Feature spec 17, phase 3: saved entry points, pending origins and routes with a rotation step. */
  entryPoints?: { count: number; pendingOrigins: number; rotations: number };
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export interface CreateNetworkRequest {
  name: string;
  settings: NetworkSettings;
  cloudAccounts: Record<string, string>;
}

/** A JSON merge patch (`If-Match`); `settings` merges member by member. */
export interface PatchNetworkRequest {
  name?: string;
  settings?: Partial<NetworkSettings> | JsonObject;
  cloudAccounts?: Record<string, string | null>;
  confirmCommitment?: { addOn: string; typed: string };
}

export interface CreateNetworkRunRequest {
  kind: RunKind;
  expectedPlanRunId?: string;
}

export interface PlannedAllocation {
  kind: AllocationKind;
  provider: string;
  region: string;
  cidr: string;
  deploymentId: string | null;
}

/** `POST /networks/validate`, `POST /networks/{id}/validate`. */
export interface NetworkValidation {
  valid: boolean;
  settingsHash: string | null;
  diagnostics: Diagnostic[];
  allocations: PlannedAllocation[];
  preselected: { cloudAccounts: Record<string, string>; allowedAccounts: Record<string, string[]> };
  impact: NetworkImpact | null;
}

export type Reachability = 'none' | 'direct' | 'via-hub' | 'via-firewall' | 'requests-only';

export interface ImpactEnd {
  deploymentId: string;
  name: string;
  provider: string;
  region: string;
}

/** Spec 18 7.5 / 15.3: shown before saving, in dry runs and on top of the network plan. */
export interface NetworkImpact {
  summary: string;
  topology: { from: TopologyKind; to: TopologyKind };
  addresses: { changed: boolean; recreatedSpokes: ImpactEnd[] };
  losesReachability: {
    a: ImpactEnd;
    b: ImpactEnd;
    before: Reachability;
    after: Reachability;
    pendingRequests: { sourceDeploymentId: string; requestId: string }[];
  }[];
  interruptions: { provider: string; text: string; severity: 'info' | 'warning' | 'danger' }[];
  costs: {
    provider: string;
    item: string;
    monthly: number;
    currency: string;
    asOf: string;
    change: 'add' | 'remove';
  }[];
  commitments: {
    addOn: string;
    label: string;
    months: number;
    monthly: number;
    currency: string;
    per: string;
    asOf: string;
  }[];
  deploymentsToApply: { deploymentId: string; name: string; reason: string }[];
  changedConnectivity: { before: Reachability; after: Reachability; count: number; pairs: string[] }[];
  resources: { provider: string; kind: string; create: number; delete: number }[];
  applyBlocks: { attachedDeployments: number; longestStep: string | null };
}

export interface SubnetUse {
  role: string;
  cidr: string;
  inUse: boolean;
  estimatedNeed: number | null;
}

/** `GET /networks/{id}/address-plan` (spec 04 6.14). */
export interface AddressPlan {
  supernets: { cidr: string; allocatedPercent: number }[];
  reserved: { cidr: string; label: string }[];
  regions: {
    provider: string;
    region: string;
    blocks: string[];
    allocatedPercent: number;
    hub: { cidr: string; subnets: SubnetUse[] } | null;
    attachments: {
      deploymentId: string;
      deploymentName: string;
      cidr: string;
      size: AttachmentSize;
      state: AllocationState;
      attachmentState: AttachmentState | null;
      subnets: SubnetUse[];
    }[];
    free: string[];
  }[];
  history: {
    cidr: string;
    state: AllocationState;
    deploymentName: string | null;
    releasedAt: string | null;
    reusableAfter: string | null;
  }[];
  diagnostics: Diagnostic[];
}

export interface ErrorRef {
  code: string;
  message: string;
}

/** `GET /networks/{id}/attachments` items. `connectivity` is in words ("isolated", "direct", "requests only"). */
export interface Attachment {
  deploymentId: string;
  deploymentName: string;
  blueprintId: string;
  blueprintName: string;
  provider: string;
  region: string;
  cidr: string;
  size: AttachmentSize;
  state: AttachmentState;
  connectivity: string;
  lastConnect: { runId: string; at: string; status: RunStatus } | null;
  lastError: ErrorRef | null;
  waitingFor: WaitingFor | null;
  requests: { in: number; out: number };
  /** Feature spec 17, phase 2: the egress declarations the spoke's last apply recorded. Absent from older API versions. */
  egress?: {
    resourceId: string;
    kind: 'fqdn' | 'service-tag';
    destination: string;
    protocol: string;
    ports: number[];
  }[];
}

export interface AttachmentListFilter {
  state?: AttachmentState | null;
  provider?: string | null;
  region?: string | null;
  q?: string | null;
}

/** `GET /networks/{id}/eligible-deployments` items (Attach deployments…). */
export interface EligibleDeployment {
  deploymentId: string;
  deploymentName: string;
  blueprintId: string;
  blueprintName: string;
  regions: {
    provider: string;
    region: string;
    cidr: string | null;
    size: AttachmentSize;
    recommendedSize: AttachmentSize;
  }[];
  impact: string;
  canAttach: boolean;
  reason: string | null;
}

export interface DnsOwner {
  deploymentId: string;
  deploymentName: string;
}

/** `GET /networks/{id}/dns`. */
export interface NetworkDns {
  zones: {
    provider: string;
    name: string;
    kind: 'private-link' | 'internal';
    zoneId: string | null;
    links: { deploymentId: string; deploymentName: string; region: string }[];
    records: {
      name: string;
      type: string;
      value: string;
      ttl: number;
      deploymentId: string;
      deploymentName: string;
      resourceId: string;
    }[];
    conflicts: { name: string; owner: DnsOwner; other: DnsOwner }[];
  }[];
}

/** `Deployment.network` (null when standalone). */
export interface DeploymentNetwork {
  id: string;
  name: string;
  topology: TopologyKind;
  mode: 'attached' | 'retained';
  attachments: {
    provider: string;
    region: string;
    cidr: string;
    size: AttachmentSize;
    state: AttachmentState;
    connectedAt: string | null;
    lastError: ErrorRef | null;
    waitingFor: WaitingFor | null;
  }[];
  /** Feature spec 17, phase 2: the deployment's request instances as source, and those targeting it. */
  requests?: DeploymentRequest[];
  inboundRequests?: DeploymentRequest[];
  /** Feature spec 17, phase 3: the deployment's origin rows on every network (its entry routes). */
  origins?: DeploymentOrigin[];
}

export type OriginState = 'applied' | 'waiting-for-apply' | 'inactive' | 'not-applied';

/** Feature spec 17, phase 3 (spec 04 6.14.10). */
export interface DeploymentOrigin {
  networkId: string;
  networkName: string;
  entryPointId: string;
  routeId: string;
  resourceId: string;
  access: OriginAccessMode;
  accessText: string;
  originState: OriginState;
  edgeUrl: string | null;
}

/** Feature spec 17, phase 3: `GET /networks/{id}/entry-points/{epId}/status` and the 202 of `rotate-origin-secret`. */
export interface EntryPointStatus {
  entryPointId: string;
  kind: string;
  label: string | null;
  tier: string | null;
  region: string | null;
  applied: boolean;
  changedSinceApply: boolean;
  hostName: string | null;
  publicIp: string | null;
  addOns: { id: string; enabled: boolean; mode: 'prevention' | 'detection' | null }[];
  domains: EntryPointDomainStatus[];
  routes: EntryPointRouteStatus[];
  pendingOrigins: { deploymentId: string; deploymentName: string; routeIds: string[] }[];
  rotations: OriginRotation[];
  certificateAccess: {
    identityPrincipalId: string | null;
    state: 'granted' | 'pending' | 'unknown';
    secretIds: string[];
  } | null;
  readAt: string | null;
  lastKnownAt: string | null;
  messages: string[];
}

export interface EntryPointDomainStatus {
  hostname: string;
  certificate: 'managed' | 'key-vault';
  state: 'validated' | 'waiting-for-dns' | 'pending' | 'failed' | 'unknown';
  /** What the user creates in their DNS. */
  records: { type: 'CNAME' | 'TXT' | 'A'; name: string; value: string }[];
}

export type OriginStrength = 'strongest' | 'strong' | 'medium' | 'weak' | 'none';

export interface EntryPointRouteStatus {
  routeId: string;
  hostnames: string[];
  paths: string[];
  origin: {
    deploymentId: string;
    deploymentName: string | null;
    resourceId: string;
    platform: string | null;
    platformLabel: string | null;
    provider: string | null;
    region: string | null;
  } | null;
  access: OriginAccessMode;
  accessText: string;
  strength: OriginStrength;
  originState: OriginState;
  inactiveReason: string | null;
  health: 'healthy' | 'unhealthy' | 'not-probed' | 'unknown';
  /** Codes of the route's settings warnings. */
  warnings: string[];
}

/** Generation numbers only, never a value. */
export interface OriginRotation {
  routeId: string;
  step: 'requested' | 'delivering' | 'switching' | 'dropping';
  edgeGeneration: number;
  newestGeneration: number | null;
  blockedBy: { deploymentId: string; deploymentName: string }[];
  requestedAt: string | null;
  requestedBy: string | null;
}

export interface RotateOriginSecretRequest {
  /** Absent: every route of the entry point with a secret. */
  routeId?: string;
}

/** Feature spec 17, phase 2 (spec 04 6.4). `state`: the Traffic tab's words. */
export interface DeploymentRequest {
  requestId: string;
  provider: string;
  region: string;
  sourceDeploymentId: string;
  sourceDeploymentName: string;
  sourceResourceId: string;
  targetDeploymentId: string | null;
  targetDeploymentName: string | null;
  targetResourceId: string;
  port: number | null;
  appliedDecision: RequestDecision | null;
  proposedDecision: Exclude<RequestDecision, 'revoked'> | null;
  enforcement: RequestEnforcement;
  enforcing: boolean;
  inactiveReason: string | null;
  state: string;
  canAskAgain: boolean;
}

/** The deployment dry run's `networkImpact` (null when `settings.network` doesn't change). */
export interface DeploymentNetworkImpact {
  networkId: string;
  networkName: string;
  change: 'attach' | 'detach' | 'release' | 'resize' | 'regions';
  regions: {
    provider: string;
    region: string;
    cidr: string | null;
    size: AttachmentSize;
    recommendedSize: AttachmentSize;
    /** Role -> estimated addresses. */
    needs: Record<string, number>;
    replaced: { resourceId: string; type: string; action: ChangeAction; impacts: string[] }[];
    interruption: string | null;
  }[];
  queue: { networkRunsAhead: number; text: string };
  /** Feature spec 17, phase 2: requests a detach, release or removed region makes inactive. */
  inactiveRequests: {
    networkId: string;
    requestId: string;
    sourceDeploymentName: string;
    targetDeploymentName: string | null;
    role: 'source' | 'target';
    text: string;
  }[];
  /** Feature spec 17, phase 2: ambiguous requests with their candidate targets (`settings.network.requestTargets`). */
  requestChoices?: {
    requestId: string;
    candidates: { deploymentId: string; name: string }[];
    suggested: string | null;
  }[];
  diagnostics: Diagnostic[];
}

// ---- Network requests (spec 04 6.14.9; feature spec 17, phase 2) ----

export type RequestDecision = 'pending' | 'approved' | 'rejected' | 'revoked';
export type RequestEnforcement = 'not-enforced' | 'opening' | 'open' | 'closing' | 'closed' | 'failed';
export type RequestPartName = 'applied' | 'proposed';
export type RequestDecisionKind = 'approve' | 'reject' | 'revoke' | 'restore';

export interface RequestPart {
  shape: { targetResourceId: string; protocol: 'tcp'; port: number };
  decision: RequestDecision;
  introducedBy: { version: number; by: string | null; planRunId?: string } | null;
}

/** One request instance; its ETag is `"v<version>"`. `state`: the Traffic tab's words. */
export interface RequestInstance {
  sourceDeploymentId: string;
  sourceDeploymentName: string;
  sourceResourceId: string;
  provider: string;
  region: string;
  targetDeploymentId: string | null;
  targetDeploymentName: string | null;
  version: number;
  applied: RequestPart | null;
  proposed: RequestPart | null;
  enforcement: RequestEnforcement;
  enforcementRunId: string | null;
  inactiveReason: string | null;
  withdrawnAt: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionReason: string | null;
  askedAgain: { at: string; by: string; reason: string } | null;
  state: string;
  effects: { approve: string | null; reject: string | null; revoke: string | null; restore: string | null };
  firewall: { region: string; used: number; limit: number } | null;
}

/** `GET /networks/{id}/requests`: one blueprint request with its instances. */
export interface RequestGroup {
  sourceBlueprint: { id: string; name: string };
  requestId: string;
  reason: string;
  target: { blueprintId: string; blueprintName: string; resourceId: string; type: string | null };
  instances: RequestInstance[];
}

/** Body of `…/{approve|reject|revoke|restore}` and `…/ask-again` (reason required except for approve). */
export interface DecideRequestBody {
  part: RequestPartName;
  reason?: string;
}

export interface BulkDecideRequestBody {
  decision: RequestDecisionKind;
  part: RequestPartName;
  reason?: string;
  instances: {
    sourceDeploymentId: string;
    requestId: string;
    provider: string;
    region: string;
    version: number;
  }[];
}

export interface BulkDecideResponse {
  instances: RequestInstance[];
  run: RunRef | null;
}

/** `extensions.changed` of a bulk call's 412. */
export interface ChangedRequest {
  sourceDeploymentId: string;
  requestId: string;
  provider: string;
  region: string;
  version: number;
  decidedBy: string | null;
}

/** The path of one request instance: `/networks/{id}/requests/{sourceDeploymentId}/{requestId}/{provider}/{region}`. */
export interface RequestInstanceKey {
  sourceDeploymentId: string;
  requestId: string;
  provider: string;
  region: string;
}

/** Filters of `GET /networks/{id}/requests` (comma-separated lists for `decision` and `enforcement`). */
export interface RequestListFilter {
  decision?: RequestDecision[];
  enforcement?: RequestEnforcement[];
  sourceBlueprintId?: string | null;
  targetBlueprintId?: string | null;
  neededForMicroSegmentation?: boolean;
}

// The kinds registry, `GET /network-kinds` (spec 18 10.1). Clients render only from it.

export type KindStatus = 'available' | 'coming-later' | 'disabled' | 'needs-provider';

export interface KindReason {
  text: string;
  action?: { label: string; href: string } | null;
}

export interface KindCost {
  monthly: number;
  currency: string;
  asOf: string;
}

export interface EdgeKind {
  kind: string;
  label: string;
  abbr: string;
  provider: NetworkProvider | null;
  scope: 'global' | 'regional' | null;
  status: KindStatus;
  reason: KindReason | null;
  tiers: { id: string; label: string; cost: KindCost | null }[];
  addOns: {
    id: string;
    label: string;
    default: boolean;
    byTier: Record<string, string> | null;
    requiresTier: string | null;
    /** The id of an entry in `NetworkKinds.commitments`. */
    commitment: string | null;
    cost: KindCost | null;
  }[];
  /** `default` null: the strongest mode is `header` or `none`, so the network admin must choose explicitly. Phase 3 adds the
   * platform's `label` and whether routes may name it (`status`, `reason`). */
  origins: {
    platform: string;
    access: string[];
    default: string | null;
    label?: string;
    status?: KindStatus;
    reason?: KindReason | null;
  }[];
  addOnSummary: string | null;
  /** Feature spec 17, phase 3: the certificate kinds its domains may use, and whether it has a host name of its own. */
  certificates?: ('managed' | 'key-vault')[];
  defaultHost?: boolean;
}

export interface ProviderKindStatus {
  status: KindStatus;
  reason: KindReason | null;
}

export interface NetworkKinds {
  edges: EdgeKind[];
  commitments: {
    addOn: string;
    edges: string[];
    label: string;
    terms: { months: number; monthly: number; currency: string; per: string; asOf: string };
  }[];
  topologies: {
    id: TopologyKind;
    label: string;
    status: KindStatus;
    reason: KindReason | null;
    /** Keys `azure`, `aws`, `gcp`. */
    byProvider: Record<string, ProviderKindStatus>;
  }[];
  firewalls: {
    provider: string;
    tiers: string[];
    status: KindStatus;
    reason: KindReason | null;
    /** Feature spec 17, phase 2: list price per region and month of each tier. */
    tierCosts?: Record<string, KindCost>;
  }[];
}

/** Activity kinds of networks (`GET /networks/{id}/activity`); attachment kinds also appear on the deployment's activity. */
export const NETWORK_ACTIVITY_KINDS = [
  'network-created',
  'network-changed',
  'network-deleted',
  'network-attached',
  'network-detached',
  'network-connected',
  'network-connect-failed',
  'network-addresses-released',
] as const;
export type NetworkActivityKind = (typeof NETWORK_ACTIVITY_KINDS)[number];

/** Feature spec 17, phase 2: activity kinds of request instances (network rows with the source deployment). The frontend part
 * adds them to the filters and sentences. */
export const NETWORK_REQUEST_ACTIVITY_KINDS = [
  'network-request-created',
  'network-request-approved',
  'network-request-rejected',
  'network-request-revoked',
  'network-request-restored',
  'network-request-asked-again',
  'network-request-stale',
  'network-request-withdrawn',
  'network-request-enforced',
  'network-request-enforcement-failed',
] as const;
export type NetworkRequestActivityKind = (typeof NETWORK_REQUEST_ACTIVITY_KINDS)[number];

/** Feature spec 17, phase 3: activity kinds of origin secrets (network rows; never a value). The frontend part adds them to the
 * filters and sentences. */
export const NETWORK_ORIGIN_ACTIVITY_KINDS = [
  'network-origin-rotation-requested',
  'network-origin-switched',
  'network-origin-regenerated',
] as const;
export type NetworkOriginActivityKind = (typeof NETWORK_ORIGIN_ACTIVITY_KINDS)[number];

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  code?: string;
  diagnostics?: Diagnostic[];
  traceId?: string;
}

export const TERMINAL_RUN_STATUSES: readonly RunStatus[] = ['completed', 'failed', 'canceled'];

export function isTerminalRunStatus(status: RunStatus): boolean {
  return TERMINAL_RUN_STATUSES.includes(status);
}
